/**
 * 🧩 답변 부품(parts) 목록 — 답변 안 특별한 덩어리를 한곳에서 정의한다.
 *
 * 부품마다: 모델이 쓰는 모양(펜스 언어·자리표시자·마크다운), 화면 부품 id, 플래그, 글로 바꾼 모양(toText).
 * toText 는 (1) 화면 부품이 그리다 실패했을 때 대신 보일 글 (2) 복사·공유·익명 기록·파일 만들기 요청에 넘길 글 둘 다에 쓴다.
 * 화면 부품 연결은 components/EnhancedMarkdown/AnswerPart.js 의 PART_COMPONENTS(같은 id).
 * 전체 표: docs/analysis/chat_overhaul_2026_10/answer_parts_catalog.md
 */
import { cardsToText, normalizeCards, replaceRichPlaceholders } from './richBlocks';
import { CHART_FENCE_LANG, chartSpecToMarkdownTable, parseChartSpec, replaceChartFences } from './chatChartSpec';
import { COPY_FENCE_LANG, fenceToMarkdown, replaceCopyFences } from './chatCopyBlocks';
import { FLASHCARDS_FENCE_LANG, inchatFenceToMarkdown, QUIZ_FENCE_LANG, replaceQuizFences } from './chatQuizSpec';
import { stripImageStripPlaceholders } from './webImageStrips';
import { replaceCalloutMarkers } from '../components/EnhancedMarkdown/remarkReading';

/** 마크다운 부품(요약 상자·단계·체크리스트) 모양. 끄면 예전 화면·복사 그대로. */
export const isAnswerPartsEnabled = () => (
  !['false', '0', 'off'].includes(String(process.env.REACT_APP_CHAT_ANSWER_PARTS || '').trim().toLowerCase())
);

// 서버가 스트림에서 숨기고 따로 보내는 펜스. 본문에 글자로 남았다면(오래된 답·서버 플래그 꺼짐) 화면에 JSON 을 보이지 않는다.
export const SERVER_ONLY_FENCE_LANGS = ['wq-followup', 'wq-next', 'clarify'];
export const CARDS_FENCE_LANG = 'wq-cards';

const parseCardLines = (code) => normalizeCards(String(code || '').split('\n').map((line) => {
  try {
    return JSON.parse(line.trim().replace(/,$/, ''));
  } catch (error) {
    return null;
  }
}));

const chartToText = ({ code }) => {
  const parsed = parseChartSpec(code);
  return parsed.ok ? chartSpecToMarkdownTable(parsed.spec) : '';
};

/**
 * 부품 정의. source: fence(```언어) | placeholder([[토큰:N]], 서버가 바꿔 넣음) | markdown(모델이 원래 쓰는 마크다운).
 * flag: 화면 쪽 플래그(없으면 항상). 서버 플래그는 문서 표에 적는다.
 */
export const ANSWER_PARTS = [
  { id: 'cards', source: 'placeholder', token: 'rich', label: '사진 카드', toText: ({ block }) => cardsToText(block?.cards) },
  { id: 'image_strip', source: 'placeholder', token: 'image_strip', label: '사진 줄', toText: () => '' },
  { id: 'chart', source: 'fence', langs: [CHART_FENCE_LANG], label: '차트', toText: chartToText },
  { id: 'copy', source: 'fence', langs: [COPY_FENCE_LANG], label: '복사용 글', toText: ({ code }) => fenceToMarkdown(code) },
  { id: 'quiz', source: 'fence', langs: [QUIZ_FENCE_LANG], flag: 'REACT_APP_CHAT_INCHAT_QUIZ', label: '대화 속 퀴즈', toText: ({ code }) => inchatFenceToMarkdown(QUIZ_FENCE_LANG, code) },
  { id: 'flashcards', source: 'fence', langs: [FLASHCARDS_FENCE_LANG], flag: 'REACT_APP_CHAT_INCHAT_QUIZ', label: '플래시카드', toText: ({ code }) => inchatFenceToMarkdown(FLASHCARDS_FENCE_LANG, code) },
  { id: 'record_text', source: 'fence', langs: ['saenggibu'], label: '생기부 글', toText: ({ code }) => String(code || '') },
  { id: 'cards_fence', source: 'fence', langs: [CARDS_FENCE_LANG], label: '사진 카드(서버 처리 전)', toText: ({ code }) => cardsToText(parseCardLines(code)) },
  { id: 'server_fence', source: 'fence', langs: SERVER_ONLY_FENCE_LANGS, label: '서버 전용 양식', toText: () => '' },
  { id: 'code', source: 'fence', langs: ['*'], label: '코드·HTML 그림', toText: ({ code }) => String(code || '') },
  { id: 'callout', source: 'markdown', label: '팁·주의 상자', toText: ({ markdown }) => replaceCalloutMarkers(markdown) },
  { id: 'summary', source: 'markdown', flag: 'REACT_APP_CHAT_ANSWER_PARTS', label: '한눈에 보기', toText: ({ markdown }) => replaceCalloutMarkers(markdown) },
  { id: 'steps', source: 'markdown', flag: 'REACT_APP_CHAT_ANSWER_PARTS', label: '단계', toText: ({ markdown }) => markdown },
  { id: 'checklist', source: 'markdown', flag: 'REACT_APP_CHAT_ANSWER_PARTS', label: '체크리스트', toText: ({ markdown }) => checklistToText(markdown) },
  { id: 'table', source: 'markdown', label: '표', toText: ({ markdown }) => markdown },
  { id: 'markdown', source: 'markdown', label: '본문 덩어리', toText: ({ markdown }) => replaceCalloutMarkers(markdown) },
];

const PART_BY_ID = new Map(ANSWER_PARTS.map((part) => [part.id, part]));
const FENCE_PART_BY_LANG = new Map(
  ANSWER_PARTS.filter((part) => part.source === 'fence').flatMap((part) => part.langs.map((lang) => [lang, part])),
);
const PLACEHOLDER_PART_BY_TOKEN = new Map(
  ANSWER_PARTS.filter((part) => part.source === 'placeholder').map((part) => [part.token, part]),
);

export const getAnswerPart = (id) => PART_BY_ID.get(id) || null;

/** 펜스 언어 → 부품. 모르는 언어는 일반 코드. */
export const findFencePart = (lang) => (
  FENCE_PART_BY_LANG.get(String(lang || '').trim().toLowerCase()) || FENCE_PART_BY_LANG.get('*')
);

/** 자리표시자 종류(rich·image_strip, 또는 splitContentByImageStrips 의 cards·strip) → 부품 */
export const findPlaceholderPart = (token) => {
  const key = token === 'cards' ? 'rich' : token === 'strip' ? 'image_strip' : token;
  return PLACEHOLDER_PART_BY_TOKEN.get(key) || null;
};

/** 부품을 글로. 정의가 없거나 toText 가 실패하면 빈 글(원문 JSON·자리표시자는 돌려주지 않는다). */
export const partToText = (id, payload = {}) => {
  const part = getAnswerPart(id);
  if (!part) return '';
  try {
    return String(part.toText(payload) || '').trim();
  } catch (error) {
    return '';
  }
};

const FENCE_OPEN_RE = /^( {0,3})(`{3,}|~{3,})[ \t]*([\w-]*)[^\n]*$/;

/**
 * 펜스 단위로 본문을 바꾼다. replace(lang, body) 가 문자열을 돌려주면 그 펜스(닫히지 않은 꼬리 포함)를 바꾸고,
 * null 이면 그대로 둔다.
 */
const replaceFences = (markdown, replace) => {
  const lines = String(markdown || '').split('\n');
  const out = [];
  for (let i = 0; i < lines.length; i += 1) {
    const open = lines[i].match(FENCE_OPEN_RE);
    if (!open) {
      out.push(lines[i]);
      continue;
    }
    const marker = open[2];
    let end = i + 1;
    while (end < lines.length) {
      const close = lines[end].match(/^ {0,3}(`{3,}|~{3,})[ \t]*$/);
      if (close && close[1][0] === marker[0] && close[1].length >= marker.length) break;
      end += 1;
    }
    const block = lines.slice(i, Math.min(end + 1, lines.length));
    const replaced = replace(open[3].toLowerCase(), lines.slice(i + 1, end).join('\n'));
    if (replaced === null) out.push(...block);
    else if (replaced) out.push(replaced);
    i = end;
  }
  return out.join('\n');
};

/** 서버 전용 펜스는 지우고, 서버가 처리하지 못한 wq-cards 펜스는 번호 목록 글로 바꾼다. */
export const replaceServerFences = (markdown) => {
  const text = typeof markdown === 'string' ? markdown : '';
  if (!/wq-(?:followup|next|cards)|clarify/i.test(text)) return text;
  return replaceFences(text, (lang, body) => {
    if (SERVER_ONLY_FENCE_LANGS.includes(lang)) return '';
    if (lang === CARDS_FENCE_LANG) return partToText('cards_fence', { code: body });
    return null;
  }).replace(/\n{3,}/g, '\n\n');
};

const TASK_ITEM_RE = /^([ \t]*)[-*+][ \t]+\[([ xX])\][ \t]+/gm;

/** 체크리스트(`- [ ] 할 일`)를 한글·카톡에 붙여 넣기 좋은 `□ 할 일` 줄로 */
export const checklistToText = (markdown) => String(markdown || '').replace(
  TASK_ITEM_RE,
  (match, indent, mark) => `${indent}${mark === ' ' ? '□' : '☑'} `,
);

/** 화면 밖에서 쓰는 본문 글(자리표시자·서버 펜스를 글로). 공유 화면은 이 글을 EnhancedMarkdown 으로 그린다. */
export const answerTextForShare = (content, richBlocks) => replaceCopyFences(replaceServerFences(
  replaceRichPlaceholders(stripImageStripPlaceholders(content), richBlocks),
));

/** 다른 AI 요청(익명 기록·PPT·학습지 개요)에 넘기는 글. 차트는 같은 숫자의 표로, 퀴즈·카드는 문제 → 정답과 해설 글로. */
export const answerTextForModel = (content, richBlocks) => replaceQuizFences(replaceChartFences(answerTextForShare(content, richBlocks)));

/** 복사·읽어 주기 글. 상자 표시도 사람이 읽는 이름표로. */
export const answerTextForCopy = (content, richBlocks) => replaceCalloutMarkers(answerTextForModel(content, richBlocks));
